/**
 * Câmera de jogo (item 4.4, GDD seção 5): atrás e acima do seu atleta, inclinada 55° para baixo, mostrando
 * cerca de 60% do campo. Ela segue a direção da vassoura com atraso e chega mais perto quando a arquibancada
 * ou uma torre ficaria entre ela e o atleta.
 */
export const CAMERA = {
  /** Inclinação para baixo, em graus (GDD). */
  pitchDeg: 55,
  fov: 48,
  /** Distância até o atleta: com 55° e 48° de abertura, mostra ~60% do campo (ver camera.test.ts). */
  distance: 50,
  /** Faixa da roda do mouse. */
  minDistance: 34,
  maxDistance: 62,
  /** Rapidez com que a câmera vira para trás da vassoura (1/s). */
  lag: 3.5,
  /** Folga acima de arquibancada e torres (m). */
  clearance: 2,
  /** Rapidez de aproximação (obstáculo) e de volta à distância normal (1/s). */
  pullIn: 14,
  pullOut: 2.5,
} as const;

export interface Vec3Like {
  x: number;
  y: number;
  z: number;
}

const PITCH_RAD = (CAMERA.pitchDeg * Math.PI) / 180;

/** Posição da câmera a `distance` do alvo, atrás dele no ângulo `theta` (0 = câmera em +z olhando para -z). */
export function cameraOffset(theta: number, distance: number): Vec3Like {
  const h = distance * Math.cos(PITCH_RAD);
  return { x: h * Math.sin(theta), y: distance * Math.sin(PITCH_RAD), z: h * Math.cos(theta) };
}

/**
 * Maior distância (até `distance`) em que o caminho do alvo até a câmera não passa por dentro de nada sólido.
 * `obstacle(x, z)` é a altura do que é sólido naquele ponto do chão.
 */
export function clearDistance(
  target: Vec3Like,
  theta: number,
  distance: number,
  obstacle: (x: number, z: number) => number,
  steps = 40,
): number {
  const o = cameraOffset(theta, distance);
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const x = target.x + o.x * t;
    const y = target.y + o.y * t;
    const z = target.z + o.z * t;
    if (y < obstacle(x, z) + CAMERA.clearance) return Math.max(0, distance * ((i - 1) / steps));
  }
  return distance;
}

/** Suaviza a distância: chega perto rápido (para não atravessar nada) e volta devagar. */
export function easeDistance(current: number, wanted: number, dt: number): number {
  const rate = wanted < current ? CAMERA.pullIn : CAMERA.pullOut;
  return current + (wanted - current) * (1 - Math.exp(-rate * Math.max(0, dt)));
}
